import { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useToken } from '../../../../providers/Token.provider';
import { usePaylink } from '../../../../providers/Paylink.provider';
import { useBank } from '../../../../providers/Bank.provider';
import { FieldData } from '../../../Form/types';
import {
  sanitizeToNumeric,
  Select,
  TextField,
  GoogleAddressField,
  FinancialInstitutionBranchesResponse,
  LinkBankAccountManuallyRequest,
  ManualBankAccountId,
  CURRENCY_SELECT_OPTIONS,
} from 'vopay';

export function buildManualBankAccountFormRows(
  Branches: FinancialInstitutionBranchesResponse,
  BankId: number,
): FieldData<LinkBankAccountManuallyRequest>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'ManualBank' });
  const { FinancialInstitution } = useBank();
  const { paylink, Token } = usePaylink();
  const { token } = useToken();

  const BranchListModified = useMemo(() => {
    const modifiedList: { [key: string]: string } = {};

    Object.values(Branches?.Branches || {}).forEach((current) => {
      modifiedList[current.BranchTransitNumber.toString()] = `<b>${current.BranchTransitNumber.toString()}</b> <br/> ${
        current.Address
      }, ${current.TownCity}, ${current.Province}, ${current.PostalCode}, Canada`;
    });

    return modifiedList;
  }, [Branches?.Branches]);

  return [
    {
      label: () => `${t('accountNumber')}`,
      fieldName: ManualBankAccountId.AccountNumber,
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: LinkBankAccountManuallyRequest) => values.AccountNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="lined" preset="number" borderColor="#C4C4C4" errorFitContent {...fieldData} />;
      },
      validate: () => Yup.mixed().required(`${t('accountNumberReqired')}`),
    },
    {
      label: () => `${t('aba')}`,
      fieldName: ManualBankAccountId.RoutingNumber,
      getInputState: () => 'required',
      showField: () => token?.Country === 'US',
      serializeField: (values: LinkBankAccountManuallyRequest) => values.RoutingNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="lined" preset="number" borderColor="#C4C4C4" {...fieldData} />;
      },
      validate: () =>
        Yup.string()
          .test('aba', `${t('abaErrorInvalid')}`, (value) => {
            const match = /^\s*([\d]{9})\s*$/.exec(value || '');
            if (!match) {
              return false;
            }

            const weights = [3, 7, 1];
            const aba = match[1];

            let sum = 0;

            for (let i = 0; i < 9; ++i) {
              // using charAt for IE7 support
              sum += Number(aba.charAt(i)) * weights[i % 3];
            }

            return token?.Country === 'US' && sum !== 0 && sum % 10 === 0;
          })
          .required(`${t('routingNumberNumberErrorReqired')}`),
    },
    {
      label: () => `${t('financialInstitutionNumber')}`,
      fieldName: ManualBankAccountId.FinancialInstitutionNumber,
      getInputState: () => 'required',
      showField: () =>
        (BankId === 0 ||
          BankId === 1894 ||
          FinancialInstitution?.FIName === 'Not Found' ||
          FinancialInstitution?.ExternalFIID === null) &&
        token?.Country !== 'US',
      serializeField: (values: LinkBankAccountManuallyRequest) => values.FinancialInstitutionNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const institutionOptions = Object.values(Branches?.FinancialInstitutions || {}).reduce(
          (previous, current) => {
            const newPair = {} as { [key: string]: string };
            newPair[current.toString()] = current.toString();
            return { ...previous, ...newPair };
          },
          {} as { [key: string]: string },
        );

        return Branches?.FinancialInstitutions ? (
          <Select variant="lined" borderColor="#C4C4C4" placeholder="" options={institutionOptions} {...fieldData} />
        ) : (
          <TextField variant="lined" preset="number" borderColor="#C4C4C4" {...fieldData} />
        );
      },
      validate: () =>
        token?.Country === 'CA'
          ? Yup.string()
              .required(`${t('financialInstitutionNumberReqired')}`)
              .max(3, `${t('financialInstitutionNumberErrorLength')}`)
          : Yup.mixed(),
    },
    {
      label: () => `${t('branchTransitNumber')}`,
      fieldName: ManualBankAccountId.BranchTransitNumber,
      getInputState: () => 'required',
      showField: () => token?.Country !== 'US',
      serializeField: (values: LinkBankAccountManuallyRequest) => values.BranchTransitNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return BankId !== 0 && BankId !== 1894 && !!FinancialInstitution ? (
          <Select
            variant="lined"
            borderColor="#C4C4C4"
            placeholder=""
            type="selectSearch"
            renderSelectedValue={(o) => o.value}
            options={BranchListModified}
            limit={100}
            enablePagination
            {...fieldData}
          />
        ) : (
          <TextField variant="lined" preset="number" borderColor="#C4C4C4" {...fieldData} errorFitContent />
        );
      },
      validate: () =>
        token?.Country === 'CA'
          ? Yup.string()
              .required(`${t('branchTransitNumberErrorReqired')}`)
              .max(5, `${t('branchTransitNumberErrorLength')}`)
          : Yup.mixed(),
    },
    {
      label: () => `${t('currency')}`,
      fieldName: ManualBankAccountId.Currency,
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: LinkBankAccountManuallyRequest) => values.Currency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            disabled={Token !== '' && paylink.Currency !== ''}
            variant="lined"
            borderColor="#C4C4C4"
            placeholder=""
            options={CURRENCY_SELECT_OPTIONS}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed(),
    },
  ];
}

export function buildAccountHolderFormRows(): FieldData<LinkBankAccountManuallyRequest>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'ManualBank' });
  const { AccountType } = useBank();
  const { paylink, Token } = usePaylink();

  return [
    {
      label: () => `${t('firstName')}`,
      fieldName: ManualBankAccountId.FirstName,
      getInputState: () => 'required',
      showField: () => AccountType === undefined || AccountType === 'personal',
      serializeField: (values: LinkBankAccountManuallyRequest) => values.FirstName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="lined" preset="text" borderColor="#C4C4C4" errorFitContent {...fieldData} />;
      },
      validate: () => Yup.string().required(`${t('firstNameErrorRequired')}`),
    },
    {
      label: () => `${t('lastName')}`,
      fieldName: ManualBankAccountId.LastName,
      getInputState: () => 'required',
      showField: () => AccountType === undefined || AccountType === 'personal',
      serializeField: (values: LinkBankAccountManuallyRequest) => values.LastName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="lined" preset="text" borderColor="#C4C4C4" errorFitContent {...fieldData} />;
      },
      validate: () => Yup.string().required(`${t('lastNameErrorReqired')}`),
    },
    {
      label: () => `${t('companyName')}`,
      fieldName: ManualBankAccountId.CompanyName,
      getInputState: () => 'required',
      showField: () => AccountType === 'business',
      serializeField: (values: LinkBankAccountManuallyRequest) => values.CompanyName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="lined" preset="text" borderColor="#C4C4C4" errorFitContent {...fieldData} />;
      },
      validate: () =>
        AccountType === 'business' ? Yup.mixed().required(`${t('companyNameErrorRequired')}`) : Yup.mixed(),
    },
    {
      label: () => `${t('email')}`,
      fieldName: ManualBankAccountId.Email,
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: LinkBankAccountManuallyRequest) => values.EmailAddress,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            disabled={Token !== '' && paylink.ReceiverEmailAddress !== ''}
            variant="lined"
            preset="text"
            borderColor="#C4C4C4"
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().email(`${t('emailErrorInvalid')}`),
    },
  ];
}

export function buildAdditionalDetailsFormRows(): FieldData<LinkBankAccountManuallyRequest>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'ManualBank' });
  const { token } = useToken();
  const country = token?.Country || 'CA';
  return [
    {
      label: () => `${t('phoneNumber')}`,
      fieldName: ManualBankAccountId.PhoneNumber,
      getInputState: () => 'required',
      showField: () => country === 'US',
      serializeField: (values: LinkBankAccountManuallyRequest) => sanitizeToNumeric(values.PhoneNumber),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="lined" preset="tel" borderColor="#C4C4C4" {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
  ];
}

export function buildAccountDetailsFormRows(): FieldData<LinkBankAccountManuallyRequest>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'ManualBank' });
  const { token } = useToken();

  return [
    {
      label: () => `${t('address')}`,
      fieldName: ManualBankAccountId.Address,
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: LinkBankAccountManuallyRequest) => values.Address,
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <GoogleAddressField
            variant="lined"
            borderColor="#C4C4C4"
            ISOCountry={['US']}
            onGoogleAddressSelect={(addressComponents) => {
              ctx.setFieldValue('Address', addressComponents.address);
              ctx.setFieldValue('City', addressComponents.city);
              ctx.setFieldValue('Province', addressComponents.province);
              ctx.setFieldValue('PostalCode', addressComponents.postalcode);
            }}
            placeholder=""
            {...fieldData}
          />
        );
      },
      validate: () => (token?.Country === 'US' ? Yup.mixed().required(`${t('valueRequired')}`) : Yup.mixed()),
    },
    {
      label: () => `${t('city')}`,
      fieldName: ManualBankAccountId.City,
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: LinkBankAccountManuallyRequest) => values.City,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="lined" preset="text" borderColor="#C4C4C4" {...fieldData} />;
      },
      validate: () => (token?.Country === 'US' ? Yup.mixed().required(`${t('valueRequired')}`) : Yup.mixed()),
    },
    {
      label: () => `${t('state')}`,
      fieldName: ManualBankAccountId.Province,
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: LinkBankAccountManuallyRequest) => values.Province,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="lined" preset="text" borderColor="#C4C4C4" {...fieldData} />;
      },
      validate: () => (token?.Country === 'US' ? Yup.mixed().required(`${t('valueRequired')}`) : Yup.mixed()),
    },
    {
      label: () => `${t('zipCode')}`,
      fieldName: ManualBankAccountId.PostalCode,
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: LinkBankAccountManuallyRequest) => values.PostalCode,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="lined" preset="text" borderColor="#C4C4C4" {...fieldData} />;
      },
      validate: () => (token?.Country === 'US' ? Yup.mixed().required(`${t('valueRequired')}`) : Yup.mixed()),
    },
  ];
}
